/**
 * Core interfaces for the Sunrise Sunset Time App
 * These interfaces define the contracts for all major components
 */

/**
 * @typedef {Object} Coordinates
 * @property {number} latitude - Latitude in decimal degrees
 * @property {number} longitude - Longitude in decimal degrees
 * @property {number} accuracy - Accuracy in meters
 * @property {Date} timestamp - When the coordinates were obtained
 */

/**
 * @typedef {Object} SolarTimes
 * @property {Date} sunrise - Sunrise time for the given date and location
 * @property {Date} sunset - Sunset time for the given date and location
 * @property {Date} date - The date for which solar times were calculated
 */

/**
 * @typedef {Object} TimeDelta
 * @property {number} hours - Hours component of the time difference
 * @property {number} minutes - Minutes component of the time difference
 * @property {boolean} isPast - Whether the reference time has passed
 * @property {string} description - Human-readable description of the delta
 */

/**
 * @typedef {Object} TimeDisplay
 * @property {string} currentTime - Formatted current time string
 * @property {TimeDelta} sunriseDelta - Time delta from sunrise
 * @property {TimeDelta} sunsetDelta - Time delta to/from sunset
 * @property {Date} lastUpdated - When the display was last updated
 * @property {LocationStatus} locationStatus - Current location service status
 */

/**
 * @typedef {Object} AppState
 * @property {boolean} isOnline - Whether the app has network connectivity
 * @property {boolean} hasLocation - Whether location is available
 * @property {boolean} solarDataAvailable - Whether solar calculations are available
 * @property {Date} lastCalculationTime - When solar data was last calculated
 * @property {string|null} errorMessage - Current error message, if any
 */

/**
 * @typedef {Object} AppConfiguration
 * @property {number} updateInterval - Update interval in milliseconds
 * @property {number} cacheExpiration - Cache expiration time in milliseconds
 * @property {number} locationTimeout - Location request timeout in milliseconds
 * @property {number} calculationAccuracy - Required calculation accuracy in minutes
 * @property {'12h'|'24h'} timeFormat - Time format preference
 */

/**
 * @enum {string}
 */
export const LocationStatus = {
  AVAILABLE: 'available',
  PENDING: 'pending',
  DENIED: 'denied',
  ERROR: 'error'
};

/**
 * TimeController interface - manages time display and updates
 * @interface
 */
export class TimeController {
  /**
   * Get the current system time
   * @returns {Date} Current date and time
   */
  getCurrentTime() {
    throw new Error('Method must be implemented');
  }

  /**
   * Start automatic time updates
   * @returns {void}
   */
  startTimeUpdates() {
    throw new Error('Method must be implemented');
  }

  /**
   * Stop automatic time updates
   * @returns {void}
   */
  stopTimeUpdates() {
    throw new Error('Method must be implemented');
  }

  /**
   * Register callback for time updates
   * @param {function(Date): void} callback - Callback function to call on time updates
   * @returns {void}
   */
  onTimeUpdate(callback) {
    throw new Error('Method must be implemented');
  }
}

/**
 * SolarCalculator interface - handles sunrise/sunset calculations
 * @interface
 */
export class SolarCalculator {
  /**
   * Calculate solar times for a given date and location
   * @param {Date} date - Date for calculation
   * @param {number} latitude - Latitude in decimal degrees
   * @param {number} longitude - Longitude in decimal degrees
   * @returns {SolarTimes} Solar times object
   */
  calculateSolarTimes(date, latitude, longitude) {
    throw new Error('Method must be implemented');
  }

  /**
   * Get time delta from sunrise
   * @param {Date} currentTime - Current time
   * @param {SolarTimes} solarTimes - Solar times for the day
   * @returns {TimeDelta} Time delta from sunrise
   */
  getSunriseDelta(currentTime, solarTimes) {
    throw new Error('Method must be implemented');
  }

  /**
   * Get time delta to/from sunset
   * @param {Date} currentTime - Current time
   * @param {SolarTimes} solarTimes - Solar times for the day
   * @returns {TimeDelta} Time delta to/from sunset
   */
  getSunsetDelta(currentTime, solarTimes) {
    throw new Error('Method must be implemented');
  }
}

/**
 * LocationService interface - manages user location
 * @interface
 */
export class LocationService {
  /**
   * Get current location from GPS
   * @returns {Promise<Coordinates>} Promise resolving to current coordinates
   */
  async getCurrentLocation() {
    throw new Error('Method must be implemented');
  }

  /**
   * Get cached location if available
   * @returns {Coordinates|null} Cached coordinates or null
   */
  getCachedLocation() {
    throw new Error('Method must be implemented');
  }

  /**
   * Request location permission from user
   * @returns {Promise<boolean>} Promise resolving to permission granted status
   */
  async requestLocationPermission() {
    throw new Error('Method must be implemented');
  }

  /**
   * Register callback for location updates
   * @param {function(Coordinates): void} callback - Callback function for location updates
   * @returns {void}
   */
  onLocationUpdate(callback) {
    throw new Error('Method must be implemented');
  }
}

/**
 * CacheService interface - provides persistent storage
 * @interface
 */
export class CacheService {
  /**
   * Store location coordinates
   * @param {Coordinates} coordinates - Coordinates to store
   * @returns {void}
   */
  storeLocation(coordinates) {
    throw new Error('Method must be implemented');
  }

  /**
   * Get stored location coordinates
   * @returns {Coordinates|null} Stored coordinates or null
   */
  getStoredLocation() {
    throw new Error('Method must be implemented');
  }

  /**
   * Store solar calculation result
   * @param {string} key - Cache key for the calculation
   * @param {SolarTimes} solarTimes - Solar times to store
   * @returns {void}
   */
  storeSolarCalculation(key, solarTimes) {
    throw new Error('Method must be implemented');
  }

  /**
   * Get stored solar calculation
   * @param {string} key - Cache key for the calculation
   * @returns {SolarTimes|null} Stored solar times or null
   */
  getSolarCalculation(key) {
    throw new Error('Method must be implemented');
  }

  /**
   * Clear expired data from cache
   * @returns {void}
   */
  clearExpiredData() {
    throw new Error('Method must be implemented');
  }
}